import { format, isValid } from "date-fns";
import { parseDisplayDate } from "@utils/safeDate";
import { formatAdaptiveDuration } from "../utils/formatAdaptiveDuration";
import {
  CANADA_COMPLETED_EVENT_IDS,
  CANADA_EXPIRED_EVENT_IDS,
  getEuropeTerminalSets,
} from "../utils/primeOrderRowClass";

export const TablePill = ({
  text,
  className,
  compact = false,
  title,
  center = false,
}: {
  text: string;
  className: string;
  compact?: boolean;
  title?: string;
  center?: boolean;
}) => (
  <div
    className={`flex items-center h-full w-full py-[2px] ${
      center ? "justify-center" : ""
    }`}
  >
    <span
      title={title ?? text}
      className={`inline-flex h-[20px] items-center justify-center gap-1 rounded px-[6px] text-[10px] font-medium ${
        compact
          ? "max-w-full whitespace-normal text-center break-words"
          : "whitespace-nowrap"
      } ${className}`}
    >
      {text}
    </span>
  </div>
);

const NEUTRAL_PILL = "bg-[#F1F3F5] text-[#686E92]";
const GREEN_PILL = "bg-[#E2F6E8] text-[#66AD7B]";
const RED_PILL = "bg-[#FFE8E8] text-[#D61C1C]";
const YELLOW_PILL = "bg-[#FFF0E4] text-[#FE961C]";

export type ActiveDurationTone = "active" | "completed" | "failed";

export const ActiveDurationPill = ({
  diffMs,
  tone,
}: {
  diffMs: number;
  tone: ActiveDurationTone;
}) => {
  const { display, tooltip } = formatAdaptiveDuration(diffMs);
  const className =
    tone === "completed"
      ? "bg-bg-status-green text-text-base"
      : tone === "failed"
        ? "bg-bg-status-red text-text-base"
        : "bg-bg-status-yellow text-text-base";

  return (
    <div
      title={tooltip}
      className={`rounded-[6px] h-[24px] min-w-[48px] px-[4px] py-[4px] text-[10px] text-text-base flex items-center justify-center whitespace-nowrap ${className}`}
    >
      {display}
    </div>
  );
};

export const readOrderField = (
  data: Record<string, unknown> | undefined | null,
  ...keys: string[]
): string | null => {
  if (!data) {
    return null;
  }

  for (const key of keys) {
    const value = data[key];
    if (value == null) {
      continue;
    }
    const asString = String(value).trim();
    if (asString !== "") {
      return asString;
    }
  }

  return null;
};

export const getTransferTypePill = (raw?: string | null) => {
  const value = (raw || "").trim();
  if (!value) {
    return { text: "--", className: NEUTRAL_PILL };
  }

  const normalized = value.toLowerCase();
  if (normalized.includes("internal")) {
    return { text: "Internal", className: GREEN_PILL };
  }
  if (normalized.includes("external")) {
    return { text: "External", className: YELLOW_PILL };
  }

  return { text: value, className: NEUTRAL_PILL };
};

export const getSettlementTypePill = (raw?: string | null) => {
  const value = (raw || "").trim();
  if (!value) {
    return { text: "--", className: NEUTRAL_PILL };
  }

  const normalized = value.toLowerCase().replace(/\s+/g, "");
  if (normalized === "instant") {
    return { text: "Instant", className: GREEN_PILL };
  }
  if (normalized === "t+1" || normalized === "t1") {
    return { text: "T+1", className: YELLOW_PILL };
  }
  if (normalized.startsWith("t+") || normalized === "tx") {
    return { text: value.replace(/\s+/g, ""), className: NEUTRAL_PILL };
  }

  return { text: value, className: NEUTRAL_PILL };
};

export const getEstSettlementTimePill = (
  raw?: string | null,
  settlementType?: string | null,
) => {
  const settlement = (settlementType || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  const value = raw == null ? "" : String(raw).trim();

  if (value.toLowerCase() === "instant") {
    return { text: "Instant", className: GREEN_PILL };
  }

  if (!value) {
    if (settlement === "instant") {
      return { text: "Instant", className: GREEN_PILL };
    }
    return { text: "Pending", className: YELLOW_PILL };
  }

  const parsed = parseDisplayDate(value);
  if (parsed && isValid(parsed)) {
    return {
      text: format(parsed, "dd/MM / HH:mm"),
      className: NEUTRAL_PILL,
    };
  }

  return { text: value, className: NEUTRAL_PILL };
};

export const getCanadaEventPillClass = (eventId?: string | number | null) => {
  const id = String(eventId ?? "");
  if (CANADA_COMPLETED_EVENT_IDS.has(id)) return GREEN_PILL;
  if (CANADA_EXPIRED_EVENT_IDS.has(id)) return RED_PILL;

  switch (id) {
    case "3":
    case "4":
    case "5":
    case "6":
    case "10":
      return YELLOW_PILL;
    default:
      return NEUTRAL_PILL;
  }
};

export const getEuropeEventPillClass = (
  eventId?: string | number | null,
  transferType?: string | null,
) => {
  const id = String(eventId ?? "");
  const { completed, expired } = getEuropeTerminalSets(transferType);

  if (completed.has(id)) return GREEN_PILL;
  if (expired.has(id)) return RED_PILL;

  switch (id) {
    case "3":
    case "4":
      return YELLOW_PILL;
    default:
      return NEUTRAL_PILL;
  }
};

export const getPhilippinesEventPillClass = (
  eventId?: string | number | null,
) => {
  const id = String(eventId ?? "");
  if (id === "4") return GREEN_PILL;
  if (id === "8" || id === "9" || id === "10") return RED_PILL;
  if (id === "2" || id === "3") return YELLOW_PILL;
  return NEUTRAL_PILL;
};

const DETAILS_PILL =
  "inline-flex h-[20px] items-center justify-center whitespace-nowrap rounded px-[6px] text-[10px] font-medium";

export const OrderDetailsPills = ({
  tradeType,
  eventId,
  eventClassName,
}: {
  tradeType?: string | null;
  eventId?: string | number | null;
  eventClassName?: string;
}) => {
  const isBuy = tradeType === "BUY";
  const hasEvent = eventId != null && String(eventId) !== "";

  return (
    <div className="flex flex-row items-center gap-[6px]">
      {hasEvent && (
        <div className={`${DETAILS_PILL} ${eventClassName ?? NEUTRAL_PILL}`}>
          Event {eventId}
        </div>
      )}
      {tradeType ? (
        <div
          className={`${DETAILS_PILL} ${isBuy ? GREEN_PILL : RED_PILL}`}
        >
          {tradeType}
        </div>
      ) : null}
    </div>
  );
};

export const getEuropeStatusDisplayText = (raw?: string | null) => {
  const value = (raw || "").trim();
  if (!value) {
    return "--";
  }

  const normalized = value.toLowerCase();
  if (normalized === "order created / payment pending") {
    return "Pending";
  }
  if (normalized === "order cancelled or expired") {
    return "Expired";
  }
  return value;
};

export const getCanadaStatusDisplayText = (raw?: string | null) => {
  const value = (raw || "").trim();
  if (!value) {
    return "--";
  }

  const normalized = value.toLowerCase();
  if (normalized === "order created / payment pending") {
    return "Pending";
  }
  if (normalized === "order cancelled or expired") {
    return "Expired";
  }
  return value;
};

export type ComplianceMarket = "canada" | "europe";

export const ComplianceCell = ({
  opsCleared,
  transferType,
  eventId,
  market,
  onClear,
}: {
  opsCleared?: number | null;
  transferType?: string | null;
  eventId?: string | number | null;
  market: ComplianceMarket;
  onClear?: () => void;
}) => {
  const isCleared =
    Number(opsCleared) === 1 ||
    (transferType || "").toLowerCase().includes("internal");

  if (isCleared) {
    return <TablePill text="Cleared" className={GREEN_PILL} />;
  }

  const clearEventId = market === "canada" ? "6" : "12";
  const canClear =
    String(eventId ?? "") === clearEventId && Number(opsCleared) === 0;

  if (canClear) {
    return (
      <div className="flex items-center h-full w-full">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          className="inline-flex h-[20px] cursor-pointer items-center justify-center whitespace-nowrap rounded bg-[#FFF0E4] px-[6px] text-[10px] font-medium text-[#FE961C]"
        >
          CLEAR
        </button>
      </div>
    );
  }

  return (
    <TablePill text="Pending" className="bg-[#F1F3F5] text-[#686E92]" />
  );
};
